import { type ErrorType } from '@framework/api-client/client/apiClient.ts';
import { useCallback } from 'react';
import { type QueryKey, type UseQueryResult, useQuery } from 'react-query';
import {
  type IUseFileDownloadApiOptionsParams,
  useFileDownloadApiOptions,
} from './useFileDownloadApiOptions.ts';

/**
 * Gestisce attraverso una callback il download di un file tramite API.
 * Utilizziamo `refetch().then(...)` anziché l'onSuccess di `useQuery`
 * perché la chiamata alla funzione di success avviene due volte, facendo
 * scaricare il file duplicato, anche con il check sul ref dei dati
 * in "useFileLocalDownload".
 */
export function useAuthDownload<
  T extends (...inputs: any[]) => any,
  TData = Awaited<ReturnType<T>>,
  TError = ErrorType<unknown>,
>(
  getQueryOptions: T,
  inputs: Parameters<T>,
  downloadApiOptions: IUseFileDownloadApiOptionsParams,
) {
  const { options, onError, onSuccess } =
    useFileDownloadApiOptions(downloadApiOptions);
  const queryOptions = getQueryOptions(...inputs, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  const { refetch } = query;

  const download = useCallback(() => {
    if (query.isFetching) return;
    refetch().then(result => {
      if (result.status === 'success') onSuccess(result.data);
      if (result.status === 'error') onError?.(result.error);
    });
  }, [onError, onSuccess, query.isFetching, refetch]);

  return { download, ...query };
}
